/**
 * The pinned-slot overlay — issue #182.
 *
 * `rooms.ts`'s `authoredTileAt` fills a lattice slot with an authored room
 * through a hash: 1 in `AUTHORED_PERIOD`, and *which* room is also a hash.
 * That is fine for "the world should feel authored throughout" and useless
 * for "the reliquary is always at the third band's west edge" — there is no
 * way to say *where* a specific drawing goes. This module is that way: a
 * small, versioned, line-based document mapping a slot's identity —
 * `(ux, uy, orient)` — to a tile name, read by `authoredTileAt` ahead of the
 * hash path.
 *
 * WHY A SIBLING FORMAT AND NOT A FOURTH `.pxm` GRAMMAR. `.pxm`
 * (`src/sim/shapeMask.ts`, `docs/29-pxm-format.md`) is a legend and a grid —
 * every one of its four shipped kinds (rigid mask, tile, weapon blade,
 * pooled tile) draws a picture. A pin document draws nothing; it is a table
 * of four columns. Stretching `.pxm`'s grid grammar to hold zero rows would
 * buy nothing but the ability to say "pxm" in the version line, and would
 * cost the one thing `.pxm` is careful about — one document, one grammar,
 * checked by one round-trip proof (`docs/29` §2) — for a document whose
 * shape does not fit it. So this format restates the *conventions* `.pxm`
 * established (a version line spelling absence as version 1, `#` header
 * comments, one canonical spelling per document, refuse-don't-repair on any
 * near miss, the parse/round-trip/resolve gate sequence `src/editor/gates.mjs`
 * runs over every authored write) rather than its grid grammar, which this
 * document has no use for.
 *
 * THE THREE OPEN QUESTIONS THE ISSUE LEFT FOR IMPLEMENTATION, ANSWERED HERE:
 *
 *  - **Slot addressing.** The document itself still speaks `(ux, uy)` — raw
 *    lattice units — because that is the one address `authoredTileAt` and
 *    `decodeTile` both already use, and a second coordinate system stored on
 *    disk is a second thing that can drift from the first. The *editor*
 *    (`worldmapSession`, `src/editor/gameSessions.ts`) is where a person
 *    reads and clicks world/band coordinates; the file underneath is unit
 *    space, same as every other lattice-addressed thing in this codebase.
 *  - **Pin vs. set-piece carving.** They coexist. A pin only ever answers
 *    `authoredTileAt`'s question — *which room, if any, goes in this body* —
 *    and stops there; whatever the world does before or after a tile is
 *    stamped (set-piece carving, edge walls, dressing) already runs over an
 *    authored tile from the hash path unchanged, so running it over a
 *    pinned one the same way is "do nothing new" rather than a feature.
 *  - **One document or one per world mode.** One, shared. Nothing in
 *    `authoredTileAt` is mode-scoped today — a slot's hash roll does not ask
 *    which mode generated it — so a pin that did would be the one thing in
 *    this call path that reads mode state, for a distinction no issue has
 *    asked for yet. `WorldGen`'s mode split can be threaded through later,
 *    behind its own document, if a concrete need for it turns up; today it
 *    would be a field nothing reads.
 *
 * DETERMINISM. A pinned slot's tile is a pure function of `(ux, uy, orient)`
 * and the pin document — no seed, no hash, no `Rng` — so it is *more*
 * deterministic than the hash path, not less: two worlds with the same pin
 * document agree on every pinned slot even if their seeds differ. An empty
 * document has no pins to answer, so every slot falls through to the hash
 * path unchanged and today's worlds are reproduced byte for byte — the
 * fallback guarantee the issue asks for, and it holds by construction rather
 * than by a special case, because `pinnedTileAt` on an empty pin list simply
 * has nothing to return.
 */

/** The two slot orientations, spelled the way the document spells them. */
export const PIN_ORIENTS = ['H', 'V'] as const;
export type PinOrient = (typeof PIN_ORIENTS)[number];

/** One `pin` line, resolved to nothing but its own four fields. */
export interface PinEntry {
  readonly ux: number;
  readonly uy: number;
  readonly orient: PinOrient;
  readonly tileName: string;
}

/** A parsed pin document: header comments, and the pins in file order. */
export interface PinDoc {
  readonly header: readonly string[];
  readonly pins: readonly PinEntry[];
}

const READER_VERSION = 1;

/**
 * The lowest version whose grammar can read this document — always 1 today,
 * the same shape `pxmVersion` has in `shapeMask.ts` for the version this
 * format has not grown past yet. Kept as a function rather than a constant
 * so a second grammar production, when one arrives, has one place to raise
 * this from rather than a version line nobody derives.
 */
export function pinmapVersion(_doc: PinDoc): number {
  return 1;
}

class PinError extends Error {}

function fail(where: string, line: number, msg: string): never {
  throw new PinError(`${where}:${line}: ${msg}`);
}

/**
 * An integer in the one spelling this format allows — restated rather than
 * imported from `shapeMask.ts`'s `intToken`, for the reason `decimalToken`'s
 * own docblock gives: two formats sharing a discipline is not two formats
 * sharing an implementation. `String(Number(tok)) === tok` rejects `007`,
 * `+7`, `-0` and `7.0` at no cost to which integers are expressible.
 */
function intToken(where: string, line: number, field: string, tok: string): number {
  const n = Number(tok);
  if (!Number.isInteger(n) || String(n) !== tok) {
    fail(where, line, `${field}: ${JSON.stringify(tok)} is not a whole number in canonical form`);
  }
  return n;
}

const TILE_NAME_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Parse a pin document.
 *
 * Strict on structure for the same reason `parseShapeMask` is: a parser that
 * accepts two spellings of one document cannot promise a round trip. Refuses
 * on the first problem rather than collecting every one — matching
 * `parseShapeMask`'s own choice, and for the same reason (see its header).
 *
 * This function checks the document is *well-formed* — every line parses,
 * every slot is pinned at most once, every tile name is a legal name shape.
 * It does **not** check that a pinned tile name exists in `authoredTiles()`
 * or that its orientation matches the pin's — that is a *resolve*-time
 * refusal (gate 3), because it needs the tile registry, and `worldmapPins.ts`
 * has and wants no dependency on `rooms.ts`. `resolvePinDoc` in `rooms.ts`
 * is where an unknown name or a mismatched orientation is caught.
 */
export function parsePinDoc(text: string, where = '<worldmap.pxm>'): PinDoc {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  let i = 0;

  let declaredVersion = 1;
  if (lines.length > 0 && lines[0].startsWith('pinmap')) {
    const vm = /^pinmap (\S+)$/.exec(lines[0]);
    if (vm === null) fail(where, 1, `expected \`pinmap <version>\`, got ${JSON.stringify(lines[0])}`);
    if (!/^[1-9][0-9]*$/.test(vm[1]) || String(Number(vm[1])) !== vm[1]) {
      fail(where, 1, `version: ${JSON.stringify(vm[1])} is not a version number in canonical form`);
    }
    declaredVersion = Number(vm[1]);
    if (declaredVersion === 1) {
      fail(where, 1, 'version 1 is spelled by leaving the `pinmap` line out, so `pinmap 1` is a second spelling of it');
    }
    if (declaredVersion > READER_VERSION) {
      fail(
        where, 1,
        `this file declares pinmap version ${declaredVersion} and this reader implements ${READER_VERSION}`,
      );
    }
    i = 1;
  }

  const header: string[] = [];
  while (i < lines.length && lines[i].startsWith('#')) header.push(lines[i++]);

  if (i < lines.length && /^pinmap( |$)/.test(lines[i])) {
    fail(where, i + 1, 'the `pinmap` version line is the first line of the file, ahead of the header comments');
  }

  const pins: PinEntry[] = [];
  const seenSlot = new Set<string>();
  for (; i < lines.length; i++) {
    const line = lines[i];
    if (line === '') fail(where, i + 1, 'blank lines are not part of this format');
    if (line.startsWith('#')) {
      fail(where, i + 1, 'every `#` header comment comes before every `pin` line');
    }
    const m = /^pin (\S+) (\S+) (\S+) (\S+)$/.exec(line);
    if (m === null) {
      fail(where, i + 1, `expected \`pin <ux> <uy> <orient> <tile-name>\`, got ${JSON.stringify(line)}`);
    }
    const ux = intToken(where, i + 1, 'ux', m[1]);
    const uy = intToken(where, i + 1, 'uy', m[2]);
    const orientTok = m[3];
    if (!(PIN_ORIENTS as readonly string[]).includes(orientTok)) {
      fail(where, i + 1, `orient: ${JSON.stringify(orientTok)} is not one of ${PIN_ORIENTS.join(', ')}`);
    }
    const orient = orientTok as PinOrient;
    const tileName = m[4];
    if (!TILE_NAME_RE.test(tileName)) {
      fail(where, i + 1, `tile name ${JSON.stringify(tileName)} is not a legal tile name`);
    }
    const slot = `${ux},${uy},${orient}`;
    if (seenSlot.has(slot)) {
      fail(where, i + 1, `slot (${ux}, ${uy}, ${orient}) is already pinned earlier in this document`);
    }
    seenSlot.add(slot);
    pins.push({ ux, uy, orient, tileName });
  }

  const doc: PinDoc = { header, pins };
  const need = pinmapVersion(doc);
  if (declaredVersion > need) {
    fail(
      where, 1,
      `this file declares pinmap version ${declaredVersion} and uses nothing newer than version ${need} — ` +
        'the version line states the lowest version that can read the file',
    );
  }
  if (declaredVersion < need) {
    fail(where, 1, `this document uses pinmap version ${need} grammar, so it must open with \`pinmap ${need}\``);
  }
  return doc;
}

/** Write a pin document back out. The inverse of `parsePinDoc`, exactly. */
export function serializePinDoc(doc: PinDoc): string {
  const version = pinmapVersion(doc);
  const out: string[] = [];
  if (version > 1) out.push(`pinmap ${version}`);
  out.push(...doc.header);
  for (const p of doc.pins) out.push(`pin ${p.ux} ${p.uy} ${p.orient} ${p.tileName}`);
  return out.length === 0 ? '' : out.join('\n') + '\n';
}

/**
 * The pin for one slot, or `undefined` if the slot has none — the lookup
 * `authoredTileAt` runs ahead of its hash path. `O(1)` off a map keyed the
 * same way `parsePinDoc` deduplicates, built once per document rather than
 * scanned per query: a world generates many more slots than a pin document
 * ever names, so the cost belongs on the (rare) load, not the (frequent) read.
 */
export function indexPinDoc(doc: PinDoc): ReadonlyMap<string, PinEntry> {
  const byslot = new Map<string, PinEntry>();
  for (const p of doc.pins) byslot.set(`${p.ux},${p.uy},${p.orient}`, p);
  return byslot;
}

export function pinKey(ux: number, uy: number, orient: PinOrient): string {
  return `${ux},${uy},${orient}`;
}
